/**
 * Opening the item editor from other screens (Phase 7b of docs/roadmap/item-addmod-react-port.md).
 * WinForms opened frmDietech_Item_AddMod over the calling form and dropped a new item straight into
 * the calling cell. The web does the same over the screen: every page has an item opener
 * (ItemEditorOpenerContext; SiteItemEditorHost in MainLayout, or the screen's own), and the buttons
 * beside item pickers open the Quick item dialog or the whole item page through it. Nothing opens a
 * new browser tab (Jeff 2026-11-03: "stay in same tab. no switching tabs.").
 *
 * Every item save is announced over a BroadcastChannel (same origin, any tab, this one too), so item
 * and size lookups refresh wherever they are.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import IconButton from '@mui/material/IconButton';
import { useGlobalStore } from '../../stores/globalStore';
import { usePermission } from '../../hooks/usePermission';
import { ITEMS_LEVEL } from './itemRules';
import { useItemEditorOpener } from './itemEditorOpener';

const CHANNEL = 'dietech-item-editor';

export type { PickedItem } from './itemEditorOpener';
import type { PickedItem } from './itemEditorOpener';

type Message =
  | ({ type: 'picked'; token: string } & PickedItem)
  | { type: 'saved'; facilityId: number; itemId: number; origin?: string };

function channel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
}

function token(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** This tab, so a listener can tell a save made here (the Quick item dialog) from one in another tab. */
const THIS_TAB = token();

/** The item page, optionally on a section: for links that go there in this tab. */
export function itemEditorUrl(itemId: number, section?: string): string {
  return `/items/${itemId}${section ? `?section=${encodeURIComponent(section)}` : ''}`;
}

/**
 * Sends a newly saved item back to the picker that asked for it: the item page opened with `?pickFor=`.
 * The site no longer opens such pages (they opened in a new tab); this answers one still open from before.
 */
export function sendPickedItem(pickFor: string, item: PickedItem): void {
  const ch = channel();
  if (!ch) return;
  ch.postMessage({ type: 'picked', token: pickFor, ...item } satisfies Message);
  ch.close();
}

/**
 * Tells every listener an item changed (the item page and the Quick item dialog, after every
 * save): other tabs, and this tab's own listeners too (a channel hears every other channel).
 */
export function announceItemSaved(facilityId: number, itemId: number): void {
  const ch = channel();
  if (!ch) return;
  ch.postMessage({ type: 'saved', facilityId, itemId, origin: THIS_TAB } satisfies Message);
  ch.close();
}

/**
 * Calls back when an item is saved somewhere else on the site: another tab, or a dialog in this
 * one (`fromThisTab`). Returns a function that stops listening.
 */
export function onItemSavedElsewhere(onSaved: (facilityId: number, itemId: number, fromThisTab: boolean) => void): () => void {
  const ch = channel();
  if (!ch) return () => undefined;
  ch.onmessage = (e: MessageEvent<Message>) => {
    if (e.data?.type === 'saved') onSaved(e.data.facilityId, e.data.itemId, e.data.origin === THIS_TAB);
  };
  return () => ch.close();
}

/**
 * Refreshes this tab's item lookups when an item is saved, here or in another tab (mounted once, near
 * the root): the item searches (a new item can be found in every item picker), the item's sizes, and
 * the item lists. Screens that show item data of their own (the Menu Cycle Workbench) listen with
 * onItemSavedElsewhere.
 */
export function ItemSavedListener() {
  const queryClient = useQueryClient();
  useEffect(() => onItemSavedElsewhere((facilityId, itemId) => {
    queryClient.invalidateQueries({ queryKey: ['lookup', 'itemSearch', facilityId] });
    queryClient.invalidateQueries({ queryKey: ['lookup', 'itemPickerSearch', facilityId] });
    queryClient.invalidateQueries({ queryKey: ['lookup', 'itemSizes', facilityId, itemId] });
    queryClient.invalidateQueries({ queryKey: ['menuCycleModify', 'itemSizes', facilityId, itemId] });
    queryClient.invalidateQueries({ queryKey: ['items', facilityId] });
    queryClient.invalidateQueries({ queryKey: ['itemSizes', facilityId] });
  }), [queryClient]);
  return null;
}

const BUTTON_SX = { width: 30, height: 34, borderRadius: '6px', ml: 0.5 } as const;

/**
 * The buttons beside an item picker, all over the screen through its item opener, never in a new tab:
 * Quick edit item (Items level 2), or Open item to view the whole item (level 1); New item, and New from
 * existing for a picked item (WinForms mbtnItemNewFromExisting), for users who can add items (level 3).
 * A new item comes back to this picker when it's saved. No opener (no Items access), no buttons.
 */
export function ItemEditorActions({ itemId, onNewItem, section, disabled }: {
  itemId: number | null | undefined;
  /** Given when the picker can take a new item. */
  onNewItem?: (item: PickedItem) => void;
  /** The item page section to open on. */
  section?: string;
  disabled?: boolean;
}) {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const { itemsLevel } = usePermission();
  const opener = useItemEditorOpener();
  if (!(facilityId > 0) || !opener) return null;
  const canEdit = itemsLevel >= ITEMS_LEVEL.MODIFY;
  const canAdd = itemsLevel >= ITEMS_LEVEL.ADD && !!onNewItem;
  const picked = itemId != null && itemId > 0 ? itemId : null;
  return (
    <>
      {picked != null && (canEdit ? (
        <IconButton size="small" sx={BUTTON_SX} title="Quick edit this item: sizes, food groups, allergy groups and production areas"
          aria-label="Quick edit item" onClick={() => opener.openItem(picked, section)}>
          <i className="bi bi-pencil-square" />
        </IconButton>
      ) : (
        <IconButton size="small" sx={BUTTON_SX} title="Open this item to look at it" aria-label="Open item"
          onClick={() => (opener.openFull ?? opener.openItem)(picked, section)}>
          <i className="bi bi-eye" />
        </IconButton>
      ))}
      {canAdd && (
        <IconButton size="small" sx={BUTTON_SX} disabled={disabled}
          title="New item: a quick item that comes back here when saved"
          aria-label="New item" onClick={() => opener.newItem(onNewItem!)}>
          <i className="bi bi-plus-square" />
        </IconButton>
      )}
      {canAdd && picked != null && (
        <IconButton size="small" sx={BUTTON_SX} disabled={disabled}
          title="New from existing: a new item that starts as a copy of this one, and comes back here when saved"
          aria-label="New item from existing" onClick={() => opener.newItem(onNewItem!, picked)}>
          <i className="bi bi-files" />
        </IconButton>
      )}
    </>
  );
}
